import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ClientModal from './ClientModal';

interface Client {
  key: number;
  title: string;
  role: string;
  image: string;
  link: string;
  whatWeDid: string;
}

interface ClientsMenuProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
}

const ClientsMenu = ({ isOpen, onClose, triggerRef }: ClientsMenuProps) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetch('/clients/clients.json')
      .then((res) => res.json())
      .then((data: Client[]) => {
        setClients(data);
      })
      .catch((err) => {
        console.error('Failed to load clients:', err);
      });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, triggerRef]);

  const handleClientClick = (client: Client) => {
    setSelectedClient(client);
    setIsModalOpen(true);
    onClose();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedClient(null);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="w-screen max-w-2xl z-50"
      style={{ left: '50%', transform: 'translateX(-50%)' }}
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden mt-1">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900">KMG Vectoral System</h3>
              <p className="text-xs text-slate-500 mt-1">Clients</p>
            </div>
            <button
              onClick={() => {
                navigate('/clients');
                onClose();
              }}
              className="text-sm text-kmg-primary hover:text-kmg-primary-dark font-medium flex items-center gap-1"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-3 mb-4">
            {clients.slice(0, 8).map((client) => (
              <button
                key={client.key}
                onClick={() => handleClientClick(client)}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-all group border border-transparent hover:border-slate-200"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-transparent border border-slate-200 p-1.5 flex items-center justify-center overflow-hidden">
                  <img
                    src={`/clients/${client.image}`}
                    alt={client.title}
                    className="w-full h-full object-contain"
                    style={{ backgroundColor: 'transparent' }}
                  />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate group-hover:text-kmg-primary transition-colors">
                    {client.title}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{client.role}</div>
                </div>
              </button>
            ))}
          </div>

          {/* See More */}
          {clients.length > 8 && (
            <button
              onClick={() => {
                navigate('/clients');
                onClose();
              }}
              className="w-full py-3 px-4 bg-slate-50 hover:bg-slate-100 rounded-lg text-sm font-medium text-slate-700 transition-colors flex items-center justify-center gap-2"
            >
              <span>See All Clients</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <ClientModal
        client={selectedClient}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default ClientsMenu;
